import type { PluginListenerHandle } from "@capacitor/core";
import { Network, type ConnectionStatus } from "@capacitor/network";
import { DialogManager } from "./ui/dialog/dialog_manager.js";
import { ConnectionFailedDialog, type ConnectionFailedVariant } from "./ui/dialog/connection_failed_dialog.js";
import { GlobalSettings } from "./global_settings.js";

type ConnectivityListener = () => void;

const REPROBE_MIN_MS = 10_000;
const REPROBE_MAX_MS = 60_000;
const PROBE_TIMEOUT_MS = 5_000;

export class NetworkManager
{
  private static initialized = false;
  private static ui_ready = false;
  private static is_offline_overlay_open = false;
  private static open_variant: ConnectionFailedVariant | null = null;
  private static is_online = true;
  private static hard_update_active = false;
  private static debug_active = false;
  private static pending_variant: ConnectionFailedVariant | null = null;
  private static online_waiters: Array<() => void> = [];
  private static network_listener: PluginListenerHandle | null = null;
  private static offline_playable_check: ( () => boolean ) | null = null;
  private static online_listeners = new Set<ConnectivityListener>();
  private static offline_listeners = new Set<ConnectivityListener>();
  private static reprobe_timer: number | null = null;
  private static reprobe_delay_ms = REPROBE_MIN_MS;
  private static probing: Promise<boolean> | null = null;
  private static open_dialog: ConnectionFailedDialog | null = null;

  public static async init(): Promise<void>
  {
    if ( this.initialized ) return;
    this.initialized = true;

    try
    {
      const status = await Network.getStatus();
      this.applyStatus( status );
      this.network_listener = await Network.addListener( "networkStatusChange", status =>
      {
        console.info( `[Network] status changed: ${status.connectionType}, connected=${status.connected}` );
        this.applyStatus( status );
      } );
      console.info( `[Network] initialized: ${status.connectionType}, connected=${status.connected}` );
    }
    catch ( error )
    {
      console.warn( "[Network] Capacitor Network unavailable, using browser events", error );
      this.is_online = navigator.onLine;
      window.addEventListener( "offline", this.handleBrowserOffline );
      window.addEventListener( "online", this.handleBrowserOnline );
      if ( !this.is_online ) this.goOffline( "os" );
    }
  }

  public static setOfflinePlayableCheck( check: () => boolean ): void
  {
    this.offline_playable_check = check;
  }

  public static addOnlineListener( listener: ConnectivityListener ): () => void
  {
    this.online_listeners.add( listener );
    return () => this.online_listeners.delete( listener );
  }

  public static addOfflineListener( listener: ConnectivityListener ): () => void
  {
    this.offline_listeners.add( listener );
    return () => this.offline_listeners.delete( listener );
  }

  public static isOfflinePlayable(): boolean
  {
    try
    {
      return this.offline_playable_check?.() ?? false;
    }
    catch ( e )
    {
      console.warn( "[Network] offline playability check failed", e );
      return false;
    }
  }

  public static onUiReady(): void
  {
    this.ui_ready = true;
    if ( this.pending_variant === "connection" && this.isOfflinePlayable() )
      this.pending_variant = null;
    if ( this.pending_variant )
    {
      const variant = this.pending_variant;
      this.pending_variant = null;
      this.showOfflineOverlay( variant );
    }
    else if ( !this.is_online && !this.isOfflinePlayable() )
    {
      this.showOfflineOverlay();
    }
  }

  public static waitForOnline(): Promise<void>
  {
    if ( this.is_online ) return Promise.resolve();
    return new Promise( resolve => this.online_waiters.push( resolve ) );
  }

  public static async retryByUser(): Promise<void>
  {
    const online = await this.probeConnectivity();
    if ( !online )
    {
      if ( !this.is_offline_overlay_open )
        this.showOfflineOverlay( this.isOfflinePlayable() ? "offline_levels_exhausted" : "connection" );
      return;
    }

    this.goOnline();
  }

  public static isOnline(): boolean
  {
    return this.is_online;
  }

  public static noteNetworkSuccess(): void
  {
    if ( this.is_online ) return;
    console.info( "[Network] a request succeeded while flagged offline — back online" );
    this.goOnline();
  }

  public static handleNetworkError( error?: unknown ): void
  {
    console.warn( "[Network] network error", error );
    if ( error instanceof Error && error.name === "ApiError" ) return;

    this.goOffline( "error" );
  }

  public static handleLoadingDataError( error?: unknown ): void
  {
    console.error( "[Network] loading error", error );
    this.showOfflineOverlay( "loading_error" );
  }

  public static showOfflineLevelsExhausted(): void
  {
    console.info( "[Network] offline level window exhausted" );
    this.showOfflineOverlay( this.is_online ? "loading_error" : "offline_levels_exhausted" );
  }

  public static showOfflineLeaderboard(): void
  {
    console.info( "[Network] leaderboard requested offline" );
    this.showOfflineOverlay( "offline_leaderboard" );
  }

  public static clearLoadingError(): void
  {
    if ( this.is_online )
      this.hideOfflineOverlay();
  }

  public static setHardUpdateActive( active = true ): void
  {
    this.hard_update_active = active;
    if ( active ) this.hideOfflineOverlay();
  }

  public static setDebugActive( active: boolean ): void
  {
    this.debug_active = active;
  }

  public static onDialogClosed( dialog: ConnectionFailedDialog ): void
  {
    if ( dialog !== this.open_dialog ) return;

    this.is_offline_overlay_open = false;
    this.open_variant = null;
    this.open_dialog = null;

    window.setTimeout( () =>
    {
      if ( !this.debug_active && !this.hard_update_active && !this.is_online && !this.isOfflinePlayable() )
        this.showOfflineOverlay();
    }, 200 );
  }

  private static applyStatus( status: ConnectionStatus ): void
  {
    if ( status.connected )
      this.goOnline();
    else
      this.goOffline( "os" );
  }

  private static goOnline(): void
  {
    const was_offline = !this.is_online;
    this.is_online = true;
    this.reprobe_delay_ms = REPROBE_MIN_MS;
    this.cancelReprobe();
    this.notifyOnlineWaiters();
    this.hideOfflineOverlay();
    if ( was_offline )
    {
      console.info( "[Network] online" );
      for ( const listener of this.online_listeners )
      {
        try { listener(); } catch ( e ) { console.warn( "[Network] online listener failed", e ); }
      }
    }
  }

  private static goOffline( source: "os" | "error" ): void
  {
    const was_online = this.is_online;
    this.is_online = false;

    if ( this.isOfflinePlayable() )
    {
      if ( was_online )
      {
        console.info( `[Network] offline (${source}) — continuing in offline mode` );
        for ( const listener of this.offline_listeners )
        {
          try { listener(); } catch ( e ) { console.warn( "[Network] offline listener failed", e ); }
        }
      }
    }
    else
    {
      this.showOfflineOverlay();
    }

    this.scheduleReprobe();
  }

  private static scheduleReprobe(): void
  {
    if ( this.reprobe_timer !== null ) return;
    this.reprobe_timer = window.setTimeout( async () =>
    {
      this.reprobe_timer = null;
      if ( this.is_online ) return;
      const online = await this.probeConnectivity();
      if ( online )
      {
        this.goOnline();
        return;
      }
      this.reprobe_delay_ms = Math.min( this.reprobe_delay_ms * 2, REPROBE_MAX_MS );
      this.scheduleReprobe();
    }, this.reprobe_delay_ms );
  }

  private static cancelReprobe(): void
  {
    if ( this.reprobe_timer === null ) return;
    window.clearTimeout( this.reprobe_timer );
    this.reprobe_timer = null;
  }

  private static probeConnectivity(): Promise<boolean>
  {
    if ( this.probing ) return this.probing;

    this.probing = ( async () =>
    {
      let os_connected: boolean;
      try
      {
        os_connected = ( await Network.getStatus() ).connected;
      }
      catch
      {
        os_connected = navigator.onLine;
      }
      if ( !os_connected ) console.info( "[Network] OS reports no connection, probing the API anyway" );

      const controller = new AbortController();
      const timeout_id = window.setTimeout( () => controller.abort(), PROBE_TIMEOUT_MS );
      try
      {
        await fetch( `${GlobalSettings.API_BASE_URL}/api/health?probe=${Date.now()}`, {
          method: "GET",
          cache: "no-store",
          signal: controller.signal,
        } );
        return true;
      }
      catch ( e )
      {
        console.info( "[Network] probe failed", e );
        return false;
      }
      finally
      {
        window.clearTimeout( timeout_id );
      }
    } )().finally( () => { this.probing = null; } );

    return this.probing;
  }

  private static notifyOnlineWaiters(): void
  {
    const waiters = this.online_waiters.splice( 0 );
    waiters.forEach( resolve => resolve() );
  }

  private static showOfflineOverlay( variant: ConnectionFailedVariant = "connection" ): void
  {
    if ( this.hard_update_active || this.debug_active ) return;

    if ( !this.ui_ready || !DialogManager.ctx )
    {
      this.pending_variant = variant;
      return;
    }

    const previous = this.open_dialog;
    if ( previous && DialogManager.isDialogOpen( previous ) )
    {
      if ( this.open_variant === variant ) return;
      previous.onDialogCloseRequested( "network_variant_change" );
    }

    const dialog = DialogManager.acquire( "ConnectionFailedDialog", variant, () => new ConnectionFailedDialog( variant ) ) as ConnectionFailedDialog;
    this.is_offline_overlay_open = true;
    this.open_variant = variant;
    this.open_dialog = dialog;
    try
    {
      if ( previous && DialogManager.isDialogOpen( previous ) )
        DialogManager.routeToDialog( dialog );
      else
        DialogManager.openDialog( dialog );
      console.info( `[Network] opened ${variant} dialog` );
    }
    catch ( error )
    {
      this.is_offline_overlay_open = false;
      this.open_variant = null;
      this.open_dialog = null;
      this.pending_variant = variant;
      console.warn( "[Network] failed to open connection dialog", error );
    }
  }

  private static hideOfflineOverlay(): void
  {
    this.pending_variant = null;
    if ( !this.is_offline_overlay_open ) return;

    const dialog = this.open_dialog;
    this.is_offline_overlay_open = false;
    this.open_variant = null;
    this.open_dialog = null;
    if ( dialog )
      DialogManager.closeDialogInstance( dialog, "network_restored" );
  }

  private static readonly handleBrowserOffline = (): void =>
  {
    this.goOffline( "os" );
  };

  private static readonly handleBrowserOnline = (): void =>
  {
    this.goOnline();
  };
}
